import React, { useState } from 'react';
import { Copy, Download, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FORMATS = [
  { key: 'square', label: 'Square (1080×1080)', use: 'Direct DM · WhatsApp chat' },
  { key: 'portrait', label: 'Portrait (1080×1350)', use: 'WhatsApp Status · Instagram feed' },
  { key: 'story', label: 'Story (1080×1920)', use: 'WhatsApp Story · Instagram Story' },
];

const BACKDROPS = [
  { key: 'bluehouse', label: 'Bluehouse · Tirthan' },
  { key: 'tirthan_river', label: 'Tirthan River' },
  { key: 'kasauli', label: 'Annie\'s Song · Kasauli' },
  { key: 'shimla', label: 'Taraview · Shimla' },
];

export default function AdminTeaser() {
  const [fmt, setFmt] = useState('square');
  const [backdrop, setBackdrop] = useState('bluehouse');
  const [reloadKey, setReloadKey] = useState(Date.now());
  const [captions, setCaptions] = useState([]);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const imgUrl = `${API}/api/admin/teaser/generate?fmt=${fmt}&backdrop=${backdrop}&_=${reloadKey}`;

  React.useEffect(() => {
    fetch(`${API}/api/admin/teaser/captions`)
      .then(r => r.json())
      .then(d => setCaptions(d.captions || []));
  }, []);

  const copyCaption = (txt, idx) => {
    navigator.clipboard.writeText(txt);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const download = async () => {
    const r = await fetch(imgUrl);
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `gohimalayan-teaser-${fmt}-${backdrop}.jpg`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400 mb-1">Content Ops</p>
          <h1 className="text-2xl font-serif text-stone-900">Teaser Creative</h1>
          <p className="text-xs text-stone-500 mt-1">Generate &amp; broadcast invitation cards to fill the Phase 1 cohort.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[1fr_440px] gap-8">
        {/* LEFT — preview */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-stone-200 p-5">
            <div className="flex flex-wrap gap-2 mb-4">
              {FORMATS.map(f => (
                <button
                  key={f.key}
                  onClick={() => setFmt(f.key)}
                  className={`px-3 py-1.5 text-xs rounded border transition ${fmt === f.key ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:border-stone-500'}`}
                  data-testid={`fmt-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-stone-100">
              {BACKDROPS.map(b => (
                <button
                  key={b.key}
                  onClick={() => setBackdrop(b.key)}
                  className={`px-3 py-1.5 text-xs rounded border transition ${backdrop === b.key ? 'bg-amber-100 text-amber-900 border-amber-400' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'}`}
                  data-testid={`backdrop-${b.key}`}
                >
                  {b.label}
                </button>
              ))}
            </div>

            {/* Preview canvas */}
            <div className="bg-stone-100 rounded p-4 flex items-center justify-center min-h-[400px]">
              <img
                key={reloadKey + fmt + backdrop}
                src={imgUrl}
                alt="Teaser preview"
                className={`shadow-lg ${fmt === 'story' ? 'max-h-[600px]' : 'max-h-[500px]'} w-auto`}
                data-testid="teaser-preview"
              />
            </div>

            <div className="flex gap-2 mt-4">
              <button onClick={download} className="flex-1 px-4 py-2.5 bg-stone-900 text-white rounded text-sm font-medium hover:bg-stone-800 flex items-center justify-center gap-2" data-testid="download-teaser">
                <Download size={16} /> Download {fmt}
              </button>
              <button onClick={() => setReloadKey(Date.now())} className="px-4 py-2.5 border border-stone-300 rounded text-sm hover:bg-stone-50 flex items-center gap-2" title="Refresh preview">
                <RefreshCw size={16} />
              </button>
            </div>

            <p className="text-[11px] text-stone-500 mt-3">
              Tip: download all 3 formats — square for DMs, portrait for Status, story for Instagram.
            </p>
          </div>
        </div>

        {/* RIGHT — captions */}
        <aside className="space-y-3">
          <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400 mb-1">Broadcast Captions</p>
          {captions.map((c, i) => (
            <div key={i} className="bg-white rounded-lg border border-stone-200 p-4" data-testid={`caption-${c.tone}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-wider uppercase text-stone-500 font-semibold">{c.tone}</span>
                <button
                  onClick={() => copyCaption(c.text, i)}
                  className="text-xs px-2 py-1 rounded hover:bg-stone-100 text-stone-700 flex items-center gap-1"
                >
                  <Copy size={12} /> {copiedIdx === i ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="text-[12px] text-stone-700 whitespace-pre-wrap font-sans leading-relaxed">{c.text}</pre>
            </div>
          ))}
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-[11px] text-amber-900 leading-relaxed">
            <b>Workflow:</b><br />
            1. Download a creative + copy a caption.<br />
            2. Send via WhatsApp broadcast list (max 256 recipients per list).<br />
            3. Hosts apply via <a href="/apply" target="_blank" rel="noopener noreferrer" className="underline">/apply</a> — submissions appear instantly in <a href="/admin/review" target="_blank" rel="noopener noreferrer" className="underline">/admin/review</a>.<br />
            4. Run audit, approve → live on <a href="/discovered" target="_blank" rel="noopener noreferrer" className="underline">/discovered</a>.
          </div>
        </aside>
      </div>
    </div>
  );
}
